import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import {
  PHASES,
  GENDER_QUESTION,
  AGE_QUESTION,
  LIFE_STAGE_QUESTION,
} from "../../data/assessment";
import { MALE_QUESTIONS } from "../../data/male_assessment";
import { AGE_IMAGES } from "../../config/landing";
import { refreshImageOverrides } from "../../hooks/useImageOverrides";
import { Upload, RotateCcw, ChevronDown, ChevronRight, Search, Download } from "lucide-react";

// Build a flat catalog of every editable image slot in the app.
// key format: `q_${questionId}_${optionId}` — must match the resolver in QuestionCard / MaleAssessment.
function buildCatalog() {
  const female = [];
  // Special: landing hero photo
  female.push({
    group: "Landing page",
    qId: "welcome",
    qTitle: "Landing hero photo",
    oId: "hero",
    oLabel: "Main welcome image",
    key: "welcome_hero",
    default: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1400&q=80",
  });
  // Special: Science-Backed Care section image (further down the landing page).
  female.push({
    group: "Landing page",
    qId: "welcome",
    qTitle: "Science-Backed Care photo",
    oId: "science",
    oLabel: "Large image in the SCIENCE-BACKED CARE section",
    key: "landing_science",
    default: "https://images.unsplash.com/photo-1595351298020-038700609878?w=1400&q=80",
  });
  // Standalone female-flow questions.
  // We intentionally SKIP AGE_QUESTION here — the age images are gender-scoped
  // at runtime (see config/landing.js → ageQuestionForGender) so we surface
  // them as 8 dedicated slots below (4 women + 4 men) using the exact keys
  // that resolveImage looks up (`q_age_band_woman_*` and `q_age_band_man_*`).
  [GENDER_QUESTION, LIFE_STAGE_QUESTION].forEach((q) => {
    q.options.forEach((o) =>
      female.push({
        group: "Woman path — start screens",
        qId: q.id,
        qTitle: q.title.en,
        oId: o.id,
        oLabel: o.label.en,
        key: `q_${q.id}_${o.id}`,
        default: o.image,
      })
    );
  });
  // Age question — WOMEN images (4 independent slots)
  AGE_QUESTION.options.forEach((o) =>
    female.push({
      group: "Age question — Women",
      qId: "age_band_woman",
      qTitle: `${AGE_QUESTION.title.en} (Women)`,
      oId: o.id,
      oLabel: o.label.en,
      key: `q_age_band_woman_${o.id}`,
      default: AGE_IMAGES.woman[o.id] || o.image,
    })
  );
  // Female PHASES
  PHASES.forEach((p) => {
    p.questions.forEach((q) => {
      q.options.forEach((o) =>
        female.push({
          group: `Woman path — ${p.title.en}`,
          qId: q.id,
          qTitle: q.title.en,
          oId: o.id,
          oLabel: o.label.en,
          key: `q_${q.id}_${o.id}`,
          default: o.image,
        })
      );
    });
  });

  const male = [];
  // Age question — MEN images (4 independent slots, kept 100% separate from Women)
  AGE_QUESTION.options.forEach((o) =>
    male.push({
      group: "Age question — Men",
      qId: "age_band_man",
      qTitle: `${AGE_QUESTION.title.en} (Men)`,
      oId: o.id,
      oLabel: o.label.en,
      key: `q_age_band_man_${o.id}`,
      default: AGE_IMAGES.man[o.id] || o.image,
    })
  );
  MALE_QUESTIONS.forEach((q) => {
    (q.options || []).forEach((o) =>
      male.push({
        group: `Man path — ${q.title.en}`,
        qId: q.id,
        qTitle: q.title.en,
        oId: o.id,
        oLabel: o.label.en,
        key: `q_${q.id}_${o.id}`,
        default: o.image,
      })
    );
  });

  return [...female, ...male];
}

function ImageSlot({ slot, currentUrl, onSet, onReset, uploading }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error("Only PNG, JPG, JPEG or WebP");
      return;
    }
    await onSet(slot, file);
  };

  const resolvedSrc = currentUrl?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${currentUrl}`
    : currentUrl || slot.default;
  const isOverride = !!currentUrl;

  return (
    <div
      className={`rounded-2xl border kt-line bg-white p-3 flex gap-3 items-center transition ${drag ? "ring-2 ring-[#D8B589]" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handleFiles(e.dataTransfer?.files);
      }}
      data-testid={`slot-${slot.key}`}
    >
      <div className="shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-[#F3F3F3] border kt-line">
        <img src={resolvedSrc} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] truncate">{slot.qTitle}</div>
        <div className="text-sm text-[#383633] truncate">{slot.oLabel}</div>
        <div className="text-[10px] font-mono text-[#A8AD94] truncate">{slot.key}</div>
      </div>
      <div className="shrink-0 flex flex-col gap-2 w-40">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`upload-${slot.key}`}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D8B589] hover:bg-[#C79E6E] text-[#3A2C1B] text-[11px] uppercase kt-tracking px-4 py-2 disabled:opacity-50"
        >
          <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload / drop"}
        </button>
        {isOverride && (
          <button
            type="button"
            onClick={() => onReset(slot)}
            className="inline-flex items-center justify-center gap-2 rounded-full border kt-line text-[#6F6B66] hover:text-[#383633] text-[11px] uppercase kt-tracking px-4 py-2"
            data-testid={`reset-${slot.key}`}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset to default
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

export default function AdminImages() {
  const catalog = useMemo(buildCatalog, []);
  const [overrides, setOverrides] = useState({});
  const [uploadingKey, setUploadingKey] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("https://scalp-assess-app.preview.emergentagent.com");
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/images/overrides`);
      setOverrides(r.data?.map || {});
    } catch (e) {
      toast.error("Could not load image overrides");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Group slots by section, honoring the search filter
  const grouped = useMemo(() => {
    const filter = q.trim().toLowerCase();
    const buckets = {};
    catalog.forEach((s) => {
      if (filter) {
        const hay = `${s.group} ${s.qTitle} ${s.oLabel} ${s.key}`.toLowerCase();
        if (!hay.includes(filter)) return;
      }
      (buckets[s.group] = buckets[s.group] || []).push(s);
    });
    return buckets;
  }, [catalog, q]);

  const toggleGroup = (g) => setOpenGroups((o) => ({ ...o, [g]: !o[g] }));
  // Open all groups by default when the user searches
  useEffect(() => {
    if (q.trim()) {
      const next = {};
      Object.keys(grouped).forEach((g) => { next[g] = true; });
      setOpenGroups(next);
    }
  }, [q, grouped]);

  const upload = async (slot, file) => {
    setUploadingKey(slot.key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/images/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (!url) throw new Error("No URL returned");
      await axios.put(`${API}/admin/images/overrides`, { key: slot.key, url });
      setOverrides((o) => ({ ...o, [slot.key]: url }));
      await refreshImageOverrides();
      toast.success("Image updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKey("");
    }
  };

  const reset = async (slot) => {
    try {
      await axios.delete(`${API}/admin/images/overrides/${encodeURIComponent(slot.key)}`);
      setOverrides((o) => {
        const n = { ...o };
        delete n[slot.key];
        return n;
      });
      await refreshImageOverrides();
      toast.success("Reset to default");
    } catch {
      toast.error("Could not reset");
    }
  };

  const runImport = async () => {
    if (!importUrl.trim().startsWith("http")) {
      toast.error("Enter a full URL, e.g. https://scalp-assess-app.preview.emergentagent.com");
      return;
    }
    setImporting(true);
    try {
      const r = await axios.post(`${API}/admin/images/import-from`, { source_url: importUrl.trim() });
      const { imported = 0, failed = 0, detail } = r.data || {};
      if (detail) toast.info(detail);
      else toast.success(`Imported ${imported} · Failed ${failed}`);
      await load();
      await refreshImageOverrides();
      setImportOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const total = catalog.length;
  const customized = Object.keys(overrides).length;

  return (
    <div className="max-w-4xl" data-testid="admin-images-page">
      <div className="mb-8">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-1">Admin</div>
        <h1 className="font-serif text-3xl text-[#383633] tracking-tight">Assessment Images</h1>
        <p className="text-sm text-[#6F6B66] mt-2 max-w-2xl">
          Upload your own PNG / JPEG / WebP photo for every quiz question. Changes go live immediately — no redeploy needed.
          Drag &amp; drop or click "Upload / drop". Max 8 MB per file.
        </p>
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-[#A8AD94]">
            {loading ? "Loading…" : `${customized} of ${total} slots customized`}
          </div>
          <button
            type="button"
            onClick={() => setImportOpen((v) => !v)}
            data-testid="import-toggle-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#E5E5E5] bg-white hover:bg-[#F3F3F3] text-[#383633] text-[11px] uppercase kt-tracking px-4 py-2"
          >
            <Download className="h-3.5 w-3.5" /> Import from preview
          </button>
        </div>
        {importOpen && (
          <div className="mt-3 border border-[#E5E5E5] rounded-2xl p-4 bg-[#F3F3F3]" data-testid="import-panel">
            <div className="text-[10px] uppercase kt-tracking text-[#6F6B66] mb-1">Source URL (the preview backend)</div>
            <input
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              data-testid="import-source-input"
              className="w-full text-sm rounded-full border border-[#E5E5E5] bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#383633]"
            />
            <p className="text-[11px] text-[#6F6B66] mt-2 leading-relaxed">
              Copies every uploaded photo from that environment into this one. Existing slots are overwritten with the source's version. No files are deleted. Safe to run multiple times.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={runImport}
                disabled={importing}
                data-testid="import-run-btn"
                className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white px-6 py-2 text-xs uppercase kt-tracking disabled:opacity-50"
              >
                {importing ? "Importing…" : "Start import"}
              </button>
              <button
                type="button"
                onClick={() => setImportOpen(false)}
                disabled={importing}
                className="text-xs uppercase kt-tracking text-[#6F6B66] hover:text-[#383633]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mb-6 relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F6B66]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by question, option or key…"
          data-testid="search-input"
          className="w-full pl-9 pr-4 py-2 rounded-full border kt-line bg-white text-sm focus-visible:outline-none focus:ring-2 focus:ring-[#D8C4A5]"
        />
      </div>

      <div className="space-y-4">
        {Object.keys(grouped).map((g) => {
          const items = grouped[g];
          const open = openGroups[g] ?? false;
          const custom = items.filter((s) => overrides[s.key]).length;
          return (
            <div key={g} className="border kt-line rounded-2xl bg-[#F8F6F2] overflow-hidden">
              <button
                type="button"
                onClick={() => toggleGroup(g)}
                data-testid={`group-toggle-${g.replace(/\s/g, "-")}`}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/50"
              >
                <div>
                  <div className="text-sm font-serif text-[#383633] tracking-tight">{g}</div>
                  <div className="text-[10px] text-[#6F6B66] uppercase kt-tracking mt-0.5">
                    {items.length} options · {custom} customized
                  </div>
                </div>
                {open ? <ChevronDown className="h-4 w-4 text-[#6F6B66]" /> : <ChevronRight className="h-4 w-4 text-[#6F6B66]" />}
              </button>
              {open && (
                <div className="p-3 space-y-2 bg-white/40">
                  {items.map((s) => (
                    <ImageSlot
                      key={s.key}
                      slot={s}
                      currentUrl={overrides[s.key]}
                      onSet={upload}
                      onReset={reset}
                      uploading={uploadingKey === s.key}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {Object.keys(grouped).length === 0 && (
          <div className="text-sm text-[#6F6B66] text-center py-8">No slots match "{q}"</div>
        )}
      </div>
    </div>
  );
}
